import { Fragment, useState } from "react";

import { CircularProgress, Stack, TextField } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { orderBy } from "lodash";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { formatIssueActivity, formatDateTime } from "./utils";
import { commentOnIssue } from "./mutations";
import { getAllPeople, getAllIssueActivity } from "./queries";

import "./CommentsAndActivity.css";
import { IssueActivityType, me } from "../../fixtures";

export interface Props {
  issueId: string;
}

export default function CommentsAndActivity({ issueId }: Props) {
  const queryClient = useQueryClient();

  const [commentText, setCommentText] = useState("");

  const commentMutation = useMutation({
    mutationFn: () => commentOnIssue(issueId, commentText),
    mutationKey: ["issue", "comment", issueId],
    onSuccess: () => {
      setCommentText("");

      queryClient.invalidateQueries({
        queryKey: ["issueActivity", issueId],
      });
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["issueActivity", issueId] });
      const previous = queryClient.getQueryData(["issueActivity", issueId]);

      queryClient.setQueryData(["issueActivity", issueId], (old: any) => {
        const updated = {
          ...old,
          activity: [
            {
              id: "issue.activity.PENDING",
              date: "Pending...",
              type: IssueActivityType.Commented,
              commentBody: commentText,
              commentingUserId: me.id,
            },
            ...old.activity,
          ],
        };

        return updated;
      });

      return { previous };
    },
    onError: (err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["issueActivity", issueId], context.previous);
      }
    },
  });

  const allPeopleQuery = useQuery({
    queryFn: getAllPeople,
    queryKey: ["allPeople"],
  });

  const allIssueActivityQuery = useQuery({
    queryFn: () => getAllIssueActivity(issueId),
    queryKey: ["issueActivity", issueId],
  });

  return (
    <Stack className="commentsAndActivity" flex={1}>
      <div
        style={{
          marginBottom: "1em",
          display: "flex",
          gap: "0.5em",
          alignItems: "center",
        }}
      >
        <div>Comments and activity</div>
        {(allPeopleQuery.isLoading || allIssueActivityQuery.isLoading) && (
          <CircularProgress size={14} />
        )}
      </div>
      {!(allPeopleQuery.isLoading || allIssueActivityQuery.isLoading) &&
        orderBy(allIssueActivityQuery.data?.activity || [], ["date"]).map(
          (issueActivity, i, allIssueActivity) => (
            <Fragment key={issueActivity.id}>
              <div className="event">
                <span className="activityDescription">
                  {formatIssueActivity(
                    issueActivity,
                    allPeopleQuery.data?.people,
                  )}
                </span>
                <span className="activityDivider">–</span>
                <span className="activityDate">
                  {formatDateTime(issueActivity.date)}
                </span>
              </div>
              {i !== allIssueActivity.length - 1 && (
                <div style={{ textAlign: "center", color: "#dad9d9" }}>|</div>
              )}
            </Fragment>
          ),
        )}
      <Stack className="activityTimeline" mb="1em"></Stack>
      <div className="comment">
        <TextField
          className="body"
          multiline
          rows={3}
          value={commentText}
          onChange={(e) => {
            setCommentText(e.target.value);
          }}
          disabled={
            commentMutation.isPending ||
            allPeopleQuery.isLoading ||
            allIssueActivityQuery.isLoading
          }
        />
        <LoadingButton
          className="add"
          variant="outlined"
          disabled={!commentText}
          loading={
            commentMutation.isPending ||
            allPeopleQuery.isLoading ||
            allIssueActivityQuery.isLoading
          }
          onClick={async () => {
            await commentMutation.mutateAsync();
          }}
        >
          Add Comment
        </LoadingButton>
      </div>
    </Stack>
  );
}
